import React, { createContext, useContext, useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

interface User {
  firstName?: string;
  profileImage?: string;
}

interface StoreInfo {
  remoteUrl: string;
  storeData: any;
}

interface UserContextType {
  user: User | null;
  setUserDetails: React.Dispatch<React.SetStateAction<User | null>>;
  globalstorevalue: string | null;
  setGlobalstorevalue: React.Dispatch<React.SetStateAction<string | null>>;
  globalStoreData: any;
  setGlobalStoreData: React.Dispatch<React.SetStateAction<any>>;
  reload: boolean;
  setReload: React.Dispatch<React.SetStateAction<boolean>>;
  handleTokenExpired: () => void;
  openChatbot: boolean;
  setOpenChatbot: React.Dispatch<React.SetStateAction<boolean>>;
  chatbotRef: React.MutableRefObject<any>;
  remoteUrl: string[];
  setRemoteUrl: React.Dispatch<React.SetStateAction<string>>;
  storeInfoMap: Record<string, StoreInfo>;
  setStoreInfoMap: React.Dispatch<React.SetStateAction<Record<string, StoreInfo>>>;
  timeZone?: string;
  activeRemoteUrl?: string;
  activeSiteId?: string;
  poCamera?: string;
  isActiveStoreHttpOnly: boolean;
}

const UserContext = createContext<UserContextType | null>(null);

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const chatbotRef = useRef(null);
  const [user, setUserDetails] = useState<User | null>(null);
  const [globalstorevalue, setGlobalstorevalue] = useState(null);
  const [globalStoreData, setGlobalStoreData] = useState<any>(null);
  const [reload, setReload] = useState<User | null>(false);
  const [timeZone, setTimeZone] = useState("");
  const [remoteUrl, setRemoteUrl] = useState<string[]>([]);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [openChatbot, setOpenChatbot] = useState(false);
  const navigate = useNavigate();
  const storedToken = localStorage.getItem("token");
  const [storeInfoMap, setStoreInfoMap] = useState<Record<string, StoreInfo>>({})
  const [posInfo, setPosInfo] = useState<string[]>([])
  const [camerasInfo, setCamerasInfo] = useState<string[]>([])

  const handleTokenExpired = () => {
    if (!hasNavigated) {
      setHasNavigated(true);
      navigate("/login");
    }
  };

  // useEffect(() => {
  //   if (!globalStoreData) return;

  //   let urls: string[] = [];

  //   if (Array.isArray(globalStoreData)) {
  //     // Multi-store
  //     urls = globalStoreData
  //       .map((store) => store.configs?.visen_ai?.remote_url)
  //       .filter(Boolean);
  //   } else {
  //     // Single store
  //     const url = globalStoreData.configs?.visen_ai?.remote_url;
  //     if (url) urls = [url];
  //   }

  //   setRemoteUrl(urls);
  //  // console.log("Updated remoteUrls:", urls);
  // }, [globalStoreData]);

  useEffect(() => {
    let firstStoreId = storeInfoMap ? Object.keys(storeInfoMap)[0] : null;
    let selectedStoreId = globalstorevalue || firstStoreId;
    if (!selectedStoreId) return;

    const fetchTimeZone = async () => {
      try {
        const res = await axios.get(
          `https://api-test.myliveeye.com/api/cust-portal/customer/stores/timezone?storeId=${selectedStoreId}`,
          {
            headers: {
              Authorization: `Bearer ${storedToken}`,
            },
          }
        );
        setTimeZone(res?.data?.timezone);
      } catch (err) {
        console.error(err);
      }
    };
    fetchTimeZone();
  }, [globalstorevalue, storeInfoMap]);

  const activeRemoteUrl = useMemo(() => {
    if (!storeInfoMap || Object.keys(storeInfoMap).length === 0) {
     // console.log("activeRemoteUrl skipped — storeInfoMap not ready yet");
      return undefined;
    }

    //  Case 1: A specific store selected
    if (globalstorevalue && globalstorevalue !== "All Stores") {
      return storeInfoMap[globalstorevalue]?.remoteUrl || undefined;
    }

    //  Case 2: "All Stores" selected → use first store’s remoteUrl
    const firstEntry = Object.entries(storeInfoMap)[0];
    if (firstEntry) {
      const [, storeInfo] = firstEntry;
      return storeInfo?.remoteUrl || undefined;
    }

    return undefined;
  }, [globalstorevalue, storeInfoMap]);

  const isActiveStoreHttpOnly = useMemo(() => {
    if (!activeRemoteUrl) return false;
    return activeRemoteUrl.startsWith("http://");
  }, [activeRemoteUrl]);

  // const activeSiteId = useMemo(() => {
  //   if (!posInfo || posInfo.length === 0) return undefined;

  //   const firstPos = posInfo[0];
  //   return firstPos?.siteId || undefined;
  // }, [posInfo]);

  // useEffect(() => {
  //   setPosInfo([]);
  // }, [globalstorevalue]);

  const posCamera = useMemo(() => {
    if (!posInfo || posInfo.length === 0) return undefined;

    const firstPos = posInfo[0];
    // console.log("First POS:", firstPos);
    // console.log("POSCAMERA:", firstPos.cameraId);
    return firstPos?.cameraId || undefined;
  }, [posInfo])

  const activeSiteId = useMemo(() => {
    if (!camerasInfo || camerasInfo.length === 0) return undefined;

    const firstCamera = camerasInfo[0];
    //console.log("First camera for activeSiteId:", firstCamera);
    return firstCamera?.siteId || undefined;

  }, [camerasInfo])

  // useEffect(() => {
  //   console.log(" Context update:");
  //   console.log("globalstorevalue:", globalstorevalue);
  //   console.log("storeInfoMap keys:", Object.keys(storeInfoMap || {}));
  //   console.log("activeRemoteUrl:", activeRemoteUrl);
  // }, [globalstorevalue, storeInfoMap, activeRemoteUrl]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUserDetails,
        globalstorevalue,
        setGlobalstorevalue,
        reload,
        setReload,
        handleTokenExpired,
        openChatbot,
        setOpenChatbot,
        chatbotRef,
        storeInfoMap,
        timeZone,
        globalStoreData,
        setGlobalStoreData,
        setStoreInfoMap,
        posInfo,
        setPosInfo,
        setCamerasInfo,
        camerasInfo,
        activeRemoteUrl,
        activeSiteId,
        posCamera,
        isActiveStoreHttpOnly,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
